import React, { FC, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { TextField } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import useStore from 'store'
import api from 'service/api'
import { userIsDirectorOrSuperAdmin } from 'utils/roles'

/**
 * Tags of a "By tags" group. The group members are the community users carrying any of the selected tags.
 * Only a director / operator loads and changes the tags (the tags API and the server both require it);
 * anybody else sees the saved tags read-only.
 */
const GroupTagsSelect: FC = () => {
  const { t } = useTranslation('groupDialog')
  const { groups, orgs } = useStore()
  const canEdit = userIsDirectorOrSuperAdmin()
  const [availableTags, setAvailableTags] = useState<string[]>([])
  const selectedTags: string[] = [...(groups.currentEditGroup?.group.groupTags ?? [])]

  useEffect(() => {
    if (!canEdit) return
    let ignore = false
    api.getUserManagementTags(orgs.currentOrgId).then((tags: any) => {
      if (!ignore && Array.isArray(tags)) {
        setAvailableTags(Array.from(new Set<string>(tags.map((tag: any) => tag.tagName))))
      }
    })
    return () => { ignore = true }
  }, [canEdit, orgs.currentOrgId])

  // a saved tag nobody carries any more still has to be shown
  const options = Array.from(new Set([...availableTags, ...selectedTags]))

  return (
    <Autocomplete
      multiple
      disabled={!canEdit}
      style={{ margin: '10px 0' }}
      options={options}
      value={selectedTags}
      onChange={(_: any, value: string[]) => groups.setCurrentEditGroupTags(value)}
      renderInput={(params) =>
        <TextField
          {...params}
          label={t`tagsLabel`}
          placeholder={t`tagsPlaceholder`}
          variant="outlined"
        />}
    />
  )
}

export default observer(GroupTagsSelect)
